import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/authContext';
import { PERMISSIONS } from '@helios/authz';
import { sessionScopeKey, useSessionScopeFence } from './useSessionScope';
import { settingsKeys } from '@/hooks/useSettings';
import { isSocketReady, releaseSocket, retainSocket } from '@/services/wsClient';
import type { RealtimeEventItem } from '@/services/realtimeClient';

function isNotificationEvent(event: RealtimeEventItem): boolean {
  if (event.eventName === 'notification.created') {
    return true;
  }
  if (event.eventName !== 'dashboard.metric.updated') {
    return false;
  }
  const payload = event.payload;
  return Boolean(
    payload &&
      typeof payload === 'object' &&
      'metric' in payload &&
      (payload as { metric?: unknown }).metric === 'notifications.unread',
  );
}

/**
 * Keeps the header bell and Settings notifications page live on every route.
 * The command-center hook still owns map/event feed refresh; this only
 * invalidates the shared notification queries.
 */
export function useNotificationRealtime(): void {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const { identity, enabled, isCurrent } = useSessionScopeFence(PERMISSIONS.NOTIFICATIONS_READ);
  const tenantId = auth.tenant?.id ?? auth.user?.tenantId ?? '';
  const userId = auth.user?.id ?? '';

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const socket = retainSocket();

    const invalidateInbox = () => {
      if (!isCurrent(identity)) return;
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.bootstrapFor(identity),
        refetchType: 'active',
      });
      void queryClient.invalidateQueries({
        queryKey: [...settingsKeys.notifications, ...sessionScopeKey(identity)],
        refetchType: 'active',
      });
    };

    const handleEvent = (event: RealtimeEventItem) => {
      if (!event?.id || event.tenantId !== tenantId) {
        return;
      }
      if (event.recipientUserId !== null && event.recipientUserId !== userId) {
        return;
      }
      if (!isNotificationEvent(event)) {
        return;
      }
      invalidateInbox();
    };

    socket.on('realtime.connected', invalidateInbox);
    socket.on('realtime.event', handleEvent);
    if (isSocketReady()) {
      invalidateInbox();
    }

    return () => {
      socket.off('realtime.connected', invalidateInbox);
      socket.off('realtime.event', handleEvent);
      releaseSocket();
    };
  }, [enabled, queryClient, tenantId, userId, identity, isCurrent]);
}
